import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { forwardRef, ReactNode, useEffect, useRef, useState } from "react";
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldErrors,
  FieldValues,
  Noop,
  Path,
  PathValue,
  UseFormSetValue,
} from "react-hook-form";
import { NumericFormat, NumericFormatProps } from "react-number-format";

import { setControllerFieldRef } from "../../helpers/form";
import { getNestedProperty } from "../../helpers/object";
import {
  ValidationOptions,
  validationOptionsToControlRules,
} from "../../types/Validations";
import APIconButton from "../buttons/APIconButton";

interface APTextFieldProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  control: Control<T>;
  validations: ValidationOptions;
  errors: FieldErrors<T>;
  defaultValue: PathValue<T, Path<T>> | undefined;
  setValue?: UseFormSetValue<T>;
  helperText?: string;
  startAdornment?: string | ReactNode;
  endAdornment?: string | ReactNode;
  placeholder?: string;
  showRequired?: boolean;
  highlightOnEmpty?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  formatNumber?: boolean;
  multiline?: boolean;
  minRows?: number;
  hidden?: boolean;
  onBlur?: (value: PathValue<T, Path<T>>) => void;
  blurOnExit?: boolean;
  size?: "small" | "medium";
  readOnlyTextMode?: boolean;
}

interface CustomNumberFormatProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const NumericFormatCustom = forwardRef<
  NumericFormatProps,
  CustomNumberFormatProps
>(function NumericFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      valueIsNumericString
    />
  );
});

const APTextField = <T extends FieldValues>({
  name,
  label,
  control,
  setValue,
  validations,
  errors,
  defaultValue,
  helperText,
  startAdornment,
  endAdornment,
  placeholder,
  showRequired,
  highlightOnEmpty,
  readOnly,
  disabled,
  clearable,
  formatNumber,
  multiline,
  minRows,
  hidden,
  onBlur,
  blurOnExit,
  size = "medium",
  readOnlyTextMode,
}: APTextFieldProps<T>) => {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!inputRef.current) return;

    if (blurOnExit) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (
              !entry.isIntersecting &&
              document.activeElement === inputRef.current
            ) {
              // blur only if it was focused and scrolled out
              inputRef.current?.blur();
            }
          });
        },
        {
          root: null, // viewport
          threshold: 0, // fire as soon as it leaves view
        }
      );

      observer.observe(inputRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [blurOnExit]);

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = (_onBlur: Noop, value: PathValue<T, Path<T>>) => {
    setFocused(false);
    onBlur && onBlur(value);
    _onBlur();
  };

  const error = getNestedProperty(name, errors);

  const renderEndAdornment = (
    field: Pick<ControllerRenderProps<T, Path<T>>, "value">
  ) => {
    if (endAdornment) {
      return endAdornment;
    } else if (clearable && setValue && field.value) {
      return (
        <APIconButton
          iconName="close"
          onClick={() => setValue(name, "" as PathValue<T, Path<T>>)}
          color="onSurface"
          size="small"
        />
      );
    }
  };

  return (
    <Controller<T>
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={validationOptionsToControlRules(label, validations)}
      render={({ field: { ref, ...field } }) => (
        <>
          {readOnlyTextMode && readOnly && (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {label && <Typography variant="bodySmall">{label}</Typography>}
              <Typography variant="bodyLarge">{field.value}</Typography>
            </Box>
          )}
          {(!readOnlyTextMode || !readOnly) && (
            <TextField
              {...field}
              required={validations.required}
              onFocus={handleFocus}
              onBlur={() => handleBlur(field.onBlur, field.value)}
              label={showRequired ? `${label} *` : label}
              fullWidth={true}
              error={!!error || (highlightOnEmpty && !field.value)}
              placeholder={placeholder}
              helperText={`${error?.message || helperText || ""}`}
              multiline={multiline}
              minRows={minRows}
              InputProps={{
                inputComponent: formatNumber
                  ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (NumericFormatCustom as any)
                  : undefined,
                startAdornment: startAdornment && (focused || field.value) && (
                  <InputAdornment position="start">
                    {startAdornment}
                  </InputAdornment>
                ),
                endAdornment: renderEndAdornment(field),
                readOnly: readOnly,
                sx: {
                  backgroundColor: "white",
                  fontSize: size === "small" ? "12px" : undefined,
                },
              }}
              inputRef={(el) => setControllerFieldRef(ref, el, inputRef)}
              disabled={disabled}
              sx={{
                display: hidden ? "none" : undefined,
              }}
              size={size}
            />
          )}
        </>
      )}
    />
  );
};

export default APTextField;
